// ===================================================================
// Web Bundles page — reads bundle data from the hidden Ghost page
// /web-bundles-data/ (same-origin, edit-to-update, no deploy needed),
// renders cards, opens modal with install steps + download.
// Zips are hosted on Ghost (storage.ghost.io), not GitHub.
// ===================================================================

// Same-origin Ghost page holding the bundle JSON in a code block.
// Lives on bmadcode.com so it can't be blocked/rate-limited like GitHub raw.
const DATA_URL = '/web-bundles-data/';

// GitHub tree — only used as the fallback link if the data page is unreachable.
const GITHUB_FALLBACK = 'https://github.com/bmad-code-org/BMAD-METHOD/tree/main/web-bundles';

// Freshness window: a bundle added/updated within this many days gets a badge.
const FRESH_DAYS = 14;

// SVG motifs — keyed by bundle.motif
const MOTIFS = {
    constellation: `
        <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="20" cy="30" r="3" fill="currentColor"/>
            <circle cx="55" cy="20" r="2.5" fill="currentColor"/>
            <circle cx="80" cy="40" r="3" fill="currentColor"/>
            <circle cx="35" cy="60" r="2.5" fill="currentColor"/>
            <circle cx="70" cy="75" r="3" fill="currentColor"/>
            <circle cx="15" cy="80" r="2" fill="currentColor"/>
            <path d="M20 30L55 20L80 40L70 75L35 60Z" opacity="0.5"/>
            <path d="M55 20L35 60" opacity="0.5"/>
            <path d="M15 80L35 60" opacity="0.5"/>
        </svg>`,
    'single-page': `
        <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" stroke-width="1.5">
            <rect x="25" y="15" width="50" height="70" rx="2" fill="currentColor" fill-opacity="0.08"/>
            <line x1="32" y1="30" x2="68" y2="30" stroke-width="1.2"/>
            <line x1="32" y1="40" x2="62" y2="40" stroke-width="1.2" opacity="0.7"/>
            <line x1="32" y1="50" x2="68" y2="50" stroke-width="1.2" opacity="0.7"/>
            <line x1="32" y1="60" x2="58" y2="60" stroke-width="1.2" opacity="0.7"/>
            <line x1="32" y1="70" x2="65" y2="70" stroke-width="1.2" opacity="0.5"/>
        </svg>`,
    'document-ribbon': `
        <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" stroke-width="1.5">
            <rect x="20" y="15" width="60" height="75" rx="2" fill="currentColor" fill-opacity="0.06"/>
            <path d="M15 35L85 35L80 45L85 55L15 55L20 45Z" fill="currentColor" fill-opacity="0.15" stroke-width="1.2"/>
            <line x1="28" y1="68" x2="72" y2="68" stroke-width="1.2" opacity="0.6"/>
            <line x1="28" y1="76" x2="65" y2="76" stroke-width="1.2" opacity="0.5"/>
        </svg>`,
    'section-stack': `
        <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" stroke-width="1.5">
            <rect x="15" y="20" width="70" height="8" rx="2" fill="currentColor" fill-opacity="0.2"/>
            <rect x="15" y="35" width="55" height="8" rx="2" fill="currentColor" fill-opacity="0.15"/>
            <rect x="15" y="50" width="65" height="8" rx="2" fill="currentColor" fill-opacity="0.15"/>
            <rect x="15" y="65" width="45" height="8" rx="2" fill="currentColor" fill-opacity="0.1"/>
            <rect x="15" y="80" width="60" height="6" rx="2" fill="currentColor" fill-opacity="0.1"/>
        </svg>`,
    'nested-layers': `
        <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" stroke-width="1.5">
            <rect x="15" y="15" width="70" height="70" rx="6" stroke-opacity="0.4"/>
            <rect x="25" y="25" width="50" height="50" rx="5" stroke-opacity="0.7"/>
            <rect x="35" y="35" width="30" height="30" rx="4" fill="currentColor" fill-opacity="0.15"/>
        </svg>`,
    'positioning-rings': `
        <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="50" cy="50" r="35" stroke-opacity="0.3"/>
            <circle cx="50" cy="50" r="22" stroke-opacity="0.5"/>
            <circle cx="50" cy="50" r="10" stroke-opacity="0.8"/>
            <circle cx="50" cy="50" r="3" fill="currentColor"/>
            <circle cx="78" cy="42" r="2.5" fill="currentColor"/>
            <circle cx="22" cy="60" r="2.5" fill="currentColor"/>
            <circle cx="62" cy="78" r="2.5" fill="currentColor"/>
            <circle cx="35" cy="22" r="2" fill="currentColor"/>
        </svg>`,
};

function motifSvg(motif) {
    return MOTIFS[motif] || MOTIFS.constellation;
}

function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    })[c]);
}

// Parse the bundle JSON out of the fetched data-page HTML. The JSON lives in a
// <pre><code> block; DOMParser + textContent decodes entities for us.
function parseBundlesFromHtml(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const blocks = Array.from(doc.querySelectorAll('pre code'));
    for (const block of blocks) {
        const text = block.textContent.trim();
        if (!text.startsWith('{')) continue;
        try {
            return JSON.parse(text);
        } catch (e) {
            // not the JSON block — keep looking
        }
    }
    throw new Error('bundles data page: no valid JSON code block found');
}

// Returns 'New' / 'Updated' / null based on the bundle's dates.
function freshnessLabel(bundle) {
    const DAY = 86400000;
    const now = Date.now();
    const added = Date.parse(bundle.addedAt);
    if (!Number.isNaN(added) && now - added >= 0 && now - added < FRESH_DAYS * DAY) return 'New';
    const updated = Date.parse(bundle.updatedAt);
    if (!Number.isNaN(updated) && now - updated >= 0 && now - updated < FRESH_DAYS * DAY) return 'Updated';
    return null;
}

function renderCard(bundle) {
    const badges = [];
    const fresh = freshnessLabel(bundle);
    if (fresh) badges.push({ text: fresh, fresh: true });
    if (bundle.needsDeepResearch) badges.push({ text: 'Deep Research' });
    if (bundle.stitchIntegration) badges.push({ text: 'Stitch' });
    const badgeHtml = badges.length
        ? `<div class="bundle-card__badges">${badges
              .map((b) => `<span class="bundle-card__badge${b.fresh ? ' bundle-card__badge--fresh' : ''}">${escapeHtml(b.text)}</span>`)
              .join('')}</div>`
        : '';

    const card = document.createElement('button');
    card.className = 'bundle-card fade-in';
    card.style.setProperty('--accent', bundle.accentColor);
    card.dataset.bundleSlug = bundle.slug;
    card.innerHTML = `
        <div class="bundle-card__motif" aria-hidden="true">${motifSvg(bundle.motif)}</div>
        <h3 class="bundle-card__name">${escapeHtml(bundle.name)}</h3>
        <p class="bundle-card__persona">${escapeHtml(bundle.defaultPersona.name)} · ${escapeHtml(bundle.defaultPersona.title)}</p>
        <p class="bundle-card__tagline">${escapeHtml(bundle.tagline)}</p>
        ${badgeHtml}
        <div class="bundle-card__stripe" aria-hidden="true"></div>
    `;
    return card;
}

function renderModalContent(bundle, isMember) {
    const dlUrl = bundle.downloadUrl;
    const knowledgeList = bundle.knowledgeFiles
        .map((f) => `<code>${escapeHtml(f)}</code>`)
        .join(', ');

    const versionLine = bundle.version
        ? `<p class="bundle-modal__version">v${escapeHtml(bundle.version)}${bundle.updatedAt ? ` · Updated ${escapeHtml(bundle.updatedAt)}` : ''}</p>`
        : '';

    const browsingStep = bundle.needsWebBrowsing
        ? `<li>Turn <strong>Web Browsing</strong> ON (the protocol verifies current references).</li>`
        : '';
    const deepResearchNotice = bundle.needsDeepResearch
        ? `<div class="bundle-modal__notice">⚡ This bundle uses <strong>Deep Research</strong>. Enable it before each session — Tools → Deep Research in both Gemini and ChatGPT. Requires Gemini Advanced or ChatGPT Plus/Pro/Business/Enterprise.</div>`
        : '';
    const stitchNote = bundle.stitchIntegration
        ? `<div class="bundle-modal__notice">🎨 Pairs with <strong>Google Stitch</strong> (stitch.withgoogle.com) — the handoff produces a Stitch prompt you copy straight from Canvas to generate editable mockups.</div>`
        : '';

    const downloadButton = isMember
        ? `<a href="${escapeHtml(dlUrl)}" class="btn-primary bundle-modal__download" download>
               <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                   <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
               </svg>
               Download .zip
           </a>`
        : `<a href="#/portal/signup" data-portal="signup" class="btn-primary bundle-modal__download">
               <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                   <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
               </svg>
               Sign up free to download
           </a>`;

    const footerText = isMember
        ? `<p class="bundle-modal__footer-text">Thanks for being a member.</p>`
        : `<p class="bundle-modal__footer-text">Free sign-up. We'll only email you when new bundles drop.</p>`;

    return `
        <header class="bundle-modal__header" style="--accent: ${escapeHtml(bundle.accentColor)}">
            <div class="bundle-modal__art">${motifSvg(bundle.motif)}</div>
            <h2 class="bundle-modal__title" id="bundle-modal-title">${escapeHtml(bundle.name)}</h2>
            <p class="bundle-modal__persona">
                <strong>${escapeHtml(bundle.defaultPersona.name)}</strong> · ${escapeHtml(bundle.defaultPersona.title)}
                <em>${escapeHtml(bundle.defaultPersona.lineage)}</em>
            </p>
            <p class="bundle-modal__tagline">${escapeHtml(bundle.tagline)}</p>
            ${versionLine}
        </header>
        <div class="bundle-modal__body">
            <p class="bundle-modal__description">${escapeHtml(bundle.description)}</p>

            <div class="bundle-modal__tabs" role="tablist" aria-label="Install platform">
                <button class="bundle-tab" role="tab" data-tab="gemini" aria-selected="true">Gemini Gem</button>
                <button class="bundle-tab" role="tab" data-tab="chatgpt" aria-selected="false">ChatGPT GPT</button>
            </div>

            <div class="bundle-tab-panel" data-tab-panel="gemini" role="tabpanel">
                <ol>
                    <li>Open <a href="https://gemini.google.com" target="_blank" rel="noopener">gemini.google.com</a> and create a new Gem named <strong>${escapeHtml(bundle.name)}</strong>.</li>
                    <li>Unzip the download and upload these as knowledge files: ${knowledgeList}.</li>
                    <li>Open <code>INSTRUCTIONS.md</code>, scroll to the <strong>PASTE BOUNDARY</strong> line, and paste everything below it into the Gem's instructions box.</li>
                    <li>Save.</li>
                </ol>
                ${deepResearchNotice}
                ${stitchNote}
            </div>

            <div class="bundle-tab-panel" data-tab-panel="chatgpt" role="tabpanel" hidden>
                <ol>
                    <li>Open <a href="https://chatgpt.com" target="_blank" rel="noopener">chatgpt.com</a> → <strong>Explore GPTs → Create</strong>. Name it <strong>${escapeHtml(bundle.name)}</strong>.</li>
                    <li>Under <strong>Configure → Knowledge</strong>, unzip and upload: ${knowledgeList}.</li>
                    <li>Paste everything below the <strong>PASTE BOUNDARY</strong> in <code>INSTRUCTIONS.md</code> into the <strong>Instructions</strong> box.</li>
                    ${browsingStep}
                    <li>Save.</li>
                </ol>
                ${deepResearchNotice}
            </div>
        </div>
        <footer class="bundle-modal__footer">
            ${footerText}
            ${downloadButton}
        </footer>
    `;
}

function openModal(bundle, isMember) {
    const modal = document.querySelector('[data-bundle-modal]');
    const content = document.querySelector('[data-bundle-modal-content]');
    if (!modal || !content) return;

    content.innerHTML = renderModalContent(bundle, isMember);
    modal.hidden = false;
    document.body.classList.add('bundle-modal-open');

    // Tab switching
    const tabs = content.querySelectorAll('.bundle-tab');
    const panels = content.querySelectorAll('.bundle-tab-panel');
    tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
            const target = tab.dataset.tab;
            tabs.forEach((t) => t.setAttribute('aria-selected', t === tab ? 'true' : 'false'));
            panels.forEach((p) => {
                p.hidden = p.dataset.tabPanel !== target;
            });
        });
    });

    // Focus the actual close button (not the backdrop div, which also has data-bundle-close)
    const closeBtn = modal.querySelector('button[data-bundle-close]');
    if (closeBtn) closeBtn.focus();
}

function closeModal() {
    const modal = document.querySelector('[data-bundle-modal]');
    if (!modal) return;
    modal.hidden = true;
    document.body.classList.remove('bundle-modal-open');
}

async function initWebBundles() {
    const grid = document.querySelector('[data-bundles-grid]');
    if (!grid) return; // not on this page

    const renderFallback = (err) => {
        grid.innerHTML = `<div class="bundle-grid__placeholder">
            Could not load bundles. <a href="${GITHUB_FALLBACK}" target="_blank" rel="noopener">Browse them on GitHub</a>.
        </div>`;
        if (err) console.error('Failed to load bundles:', err);
    };

    let manifest;
    try {
        const res = await fetch(DATA_URL, { cache: 'no-cache' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const html = await res.text();
        manifest = parseBundlesFromHtml(html);
    } catch (err) {
        renderFallback(err);
        return;
    }

    if (!manifest || !Array.isArray(manifest.bundles) || manifest.bundles.length === 0) {
        renderFallback(new Error('bundles data has unexpected shape: missing non-empty "bundles" array'));
        return;
    }

    const isMember = !!window.__ghostMember;

    // Version / count line
    const versionEl = document.querySelector('[data-bundles-version]');
    if (versionEl) {
        const count = manifest.bundles.length;
        const updated = manifest.updatedAt ? ` · Updated ${manifest.updatedAt}` : '';
        versionEl.textContent = `${count} planning agent${count === 1 ? '' : 's'}${updated}`;
    }

    // Render cards
    grid.innerHTML = '';
    manifest.bundles.forEach((bundle) => {
        const card = renderCard(bundle);
        card.addEventListener('click', () => openModal(bundle, isMember));
        grid.appendChild(card);
        // Trigger fade-in animation
        requestAnimationFrame(() => card.classList.add('is-visible'));
    });

    // Modal close handlers
    document.querySelectorAll('[data-bundle-close]').forEach((el) => {
        el.addEventListener('click', closeModal);
    });
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeModal();
    });
}

export { initWebBundles };
